import React from 'react';
import * as XLSX from 'xlsx';
import { getInventory } from '../utils/storage';

const Info: React.FC = () => {
  const handleExportXls = () => {
    const inventory = getInventory();
    const rows = inventory.map((p) => ({
      ID: p.id,
      Name: p.name,
      Quantity: p.quantity,
      'Expiry Date': p.expiryDate || '',
      'Base Code': p.baseCode || '',
      'Barcode Format': p.barcodeFormat || '',
      Category: p.category || '',
      'Date Added': p.dateAdded,
      'Last Updated': p.lastUpdated
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory');
    const pad = (n: number) => n.toString().padStart(2, '0');
    const now = new Date();
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const filename = `magazyn-export_${timestamp}.xls`;
    XLSX.writeFile(workbook, filename, { bookType: 'xls' });
  };
  return (
    <div className="app-container info-container">
      <h2>Information</h2>
      <div className="info-content">
        <h3>About the app</h3>
        <p>Stockapp by INSPLY - an application for inventory management using barcode and Data Matrix scanning.</p>
        <p>Version: 1.0.0</p>
        
        <h3 className="mt-4">How does the app work?</h3>
        <div className="flow-steps">
          <div className="flow-step">
            <div className="flow-step-number">1</div>
            <div className="flow-step-content">
              <h4>Scan the product</h4>
              <p>Go to the "Scan" tab and scan the product barcode or Data Matrix using your device camera.</p>
            </div>
          </div>
          
          <div className="flow-step">
            <div className="flow-step-number">2</div>
            <div className="flow-step-content">
              <h4>New product</h4>
              <p>If a product is scanned for the first time, you will be asked to provide the product name, quantity, expiry date (see the rules below), and optionally a category.</p>
            </div>
          </div>
          
          <div className="flow-step">
            <div className="flow-step-number">3</div>
            <div className="flow-step-content">
              <h4>Existing product</h4>
              <p>If the product already exists (matched by its base code and expiry date), you will see a modal with options to add more or consume the product.</p>
            </div>
          </div>
          
          <div className="flow-step">
            <div className="flow-step-number">4</div>
            <div className="flow-step-content">
              <h4>Inventory management</h4>
              <p>In the "Stock" tab, you can browse, search, and edit all products in the database.</p>
            </div>
          </div>
          
          <div className="flow-step">
            <div className="flow-step-number">5</div>
            <div className="flow-step-content">
              <h4>Notifications</h4>
              <p>The app will automatically notify you when a product quantity drops below 5 units.</p>
            </div>
          </div>
        </div>

        <h3 className="mt-4">Expiry date and product identity</h3>
        <p>The app treats the combination of base code and expiry date as the product identity. The final ID is built as <code>baseCode|exp:YYYY-MM-DD</code> (or just <code>baseCode</code> if no expiry is present). This means the same product with different expiry dates is tracked as separate items.</p>
        <ul>
          <li><strong>Data Matrix (GS1)</strong>: The scanner attempts to read GTIN (<code>(01)</code>) and expiry date (<code>(17)</code>, YYMMDD). If an expiry is found, it is pre-filled in the form and can be edited before saving.</li>
          <li><strong>EAN-13 / EAN-8</strong>: The expiry date is not encoded in the barcode. You must enter it manually in the form.</li>
          <li><strong>Missing expiry</strong>: If the code does not contain an expiry date, you will be required to provide one in the form.</li>
          <li><strong>Avoiding duplicates</strong>: When you submit the form, the final ID is recomputed from the base code and expiry date. If an item with that ID already exists, the app opens the action modal instead of creating a duplicate.</li>
        </ul>

        <h3 className="mt-4">Existing vs new products</h3>
        <ul>
          <li><strong>Existing</strong>: Scanning a code that matches an existing ID opens a modal where you can increase or decrease the quantity by a chosen amount.</li>
          <li><strong>New</strong>: If there is no match, a form opens so you can provide the name, quantity, expiry date and category, then save the product.</li>
        </ul>

        <h3 className="mt-4">Categories</h3>
        <p>Each product can have an optional category. The category field supports suggestions based on previously used categories. Categories help filter products in the "Stock" tab.</p>
        
        <h3 className="mt-4">Data storage</h3>
        <p>All data is stored locally on your device in the browser storage (localStorage). The application also works offline as a Progressive Web App (PWA).</p>
        <div className="mt-4">
          <button className="btn btn-primary" onClick={handleExportXls}>
            Export inventory to Excel (.xls)
          </button>
        </div>
        
        <h3 className="mt-4">Tips</h3>
        <ul>
          <li>For best scanning results, ensure the code is within the target area and well-lit.</li>
          <li>You can install the app on your device home screen using the "Add to Home Screen" option in your browser.</li>
          <li>Click on a low stock notification to quickly replenish inventory.</li>
        </ul>
      </div>
    </div>
  );
};

export default Info; 